import { useState } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckIcon from '@mui/icons-material/Check';
import { api } from '../api';
import { XxllncAiLogo, McpIcon } from '../components/icons';
import { useBranding } from '../contexts/BrandingContext';
import { TIERS as BASE_TIERS } from '../constants';
import { useTranslation } from '../i18n/useTranslation';
import type { TranslationKey } from '../i18n';

const TIER_TAGLINE_KEYS: Record<string, TranslationKey> = {
  free: 'registration.tier.free.tagline',
  team: 'registration.tier.team.tagline',
  enterprise: 'registration.tier.enterprise.tagline',
};

// Keep these counts in sync with TIER_LIMITS in secureaccess-api/app/api/portal/portal_domain.py.
const TIERS = BASE_TIERS.map((tier) => ({
  tier: tier.key as 'free' | 'team' | 'enterprise',
  labelKey: tier.labelKey,
  taglineKey: TIER_TAGLINE_KEYS[tier.key] as TranslationKey,
  featureKeys: tier.featureKeys,
  accent: tier.color,
  accentLight: tier.bgColor,
}));

interface Props {
  onSubmitted: () => void;
}

export default function RegistrationPage({ onSubmitted }: Props) {
  const { logout, getAccessTokenSilently } = useOidcContext();
  const t = useTranslation();
  const branding = useBranding();
  const [selected, setSelected] = useState<'free' | 'team' | 'enterprise' | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const portalTitle = branding.portal_title || 'MCP Hub';

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.requestPlatformTier(token, selected, message || undefined);
      onSubmitted();
    } catch {
      setError(t('errors.tryAgain'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid rgba(0,0,0,0.07)',
          px: 4,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {branding.logo_url ? (
            <Box
              component="img"
              src={branding.logo_url}
              alt="logo"
              sx={{ maxHeight: '2rem', maxWidth: 160, width: 'auto' }}
            />
          ) : (
            <XxllncAiLogo height={24} />
          )}
          <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(0,0,0,0.1)', mx: 0.5 }} />
          <McpIcon sx={{ fontSize: 16, color: 'text.primary' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.primary', letterSpacing: '-0.01em' }}>
            {portalTitle}
          </Typography>
        </Box>
        <Tooltip title={t('common.logoutTooltip')}>
          <IconButton
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            size="small"
            sx={{ color: 'text.disabled' }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 8,
        }}
      >
        <Box sx={{ maxWidth: 800, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'action.hover',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '20px',
                px: 2,
                py: 0.75,
                mb: 3,
              }}
            >
              <McpIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t('registration.welcome', { title: portalTitle })}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: { xs: '1.75rem', sm: '2.25rem' },
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: 'text.primary',
                lineHeight: 1.15,
                mb: 2,
              }}
            >
              {t('registration.heading', { title: portalTitle })}
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: 'text.secondary', maxWidth: 480, mx: 'auto', lineHeight: 1.6 }}>
              {t('registration.subheading')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2,
              mb: 5,
            }}
          >
            {TIERS.map((tier) => {
              const isSelected = selected === tier.tier;
              return (
                <Box
                  key={tier.tier}
                  onClick={() => setSelected(tier.tier)}
                  sx={{
                    position: 'relative',
                    border: isSelected ? `2px solid ${tier.accent}` : '2px solid rgba(0,0,0,0.08)',
                    borderRadius: '14px',
                    p: 3,
                    cursor: 'pointer',
                    bgcolor: isSelected ? tier.accentLight : '#fff',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: isSelected ? tier.accent : 'rgba(0,0,0,0.2)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: isSelected ? 'none' : '2px solid rgba(0,0,0,0.15)',
                      bgcolor: isSelected ? tier.accent : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {isSelected && <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                      mb: 0.5,
                      pr: 3,
                    }}
                  >
                    {t(tier.labelKey)}
                  </Typography>

                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2.5, lineHeight: 1.4 }}>
                    {t(tier.taglineKey)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {tier.featureKeys.map((featureKey) => (
                      <Box key={featureKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            bgcolor: isSelected ? tier.accent : 'text.disabled',
                            flexShrink: 0,
                            transition: 'all 0.15s ease',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            color: isSelected ? 'text.primary' : 'text.secondary',
                            fontWeight: isSelected ? 500 : 400,
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {t(featureKey)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ mb: 5 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={t('registration.messagePlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              variant="outlined"
              inputProps={{ maxLength: 1000 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.25)' },
                  '&.Mui-focused fieldset': { borderColor: '#31679B' },
                },
              }}
            />
          </Box>
          {error && (
            <Typography sx={{ color: 'error.main', fontSize: '0.875rem', mb: 2, textAlign: 'center' }}>
              {error}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              disabled={!selected || submitting}
              onClick={handleSubmit}
              sx={{
                bgcolor: 'primary.main',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.9rem',
                px: 5,
                py: 1.5,
                borderRadius: '12px',
                textTransform: 'none',
                letterSpacing: '-0.01em',
                minWidth: 200,
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(0,0,0,0.08)',
                  color: 'rgba(0,0,0,0.3)',
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.7)' }} />
              ) : (
                selected === 'free' ? t('registration.submitButtonFree') : t('registration.submitButton')
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
